/// <reference types="vite/client" />
import React, { useEffect } from "react";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import "@/styles.css";
import "@/i18n";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { OfflineBanner } from "@/components/OfflineBanner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AccountProvider } from "@/contexts/AccountContext";
import { MobileMenuProvider } from "@/contexts/MobileMenuContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { ArtifactsProvider } from "@/contexts/ArtifactsContext";
import { RightPanelProvider } from "@/contexts/RightPanelContext";
import ArtifactsHost from "@/components/ArtifactsHost";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ImpersonationBanner from "@/components/layout/ImpersonationBanner";
import PendingDeepLinkConsumer from "@/components/layout/PendingDeepLinkConsumer";
import BulkConfirmHost from "@/components/BulkConfirmHost";
import ScrollToHash from "@/components/ScrollToHash";
import GdprConsentHost from "@/components/GdprConsentHost";
import ConfirmDialogHost from "@/components/ConfirmDialogHost";
import PromptDialogHost from "@/components/PromptDialogHost";
import SenderPickHost from "@/components/SenderPickHost";
import TemplatePickerHost from "@/components/TemplatePickerHost";
import CoverFieldsHost from "@/components/CoverFieldsHost";
import ExportDestinationHost from "@/components/ExportDestinationHost";
import CloudTemplateExportHost from "@/components/export/CloudTemplateExportHost";
import ConfirmRawExportHost from "@/components/export/ConfirmRawExportHost";
import GammaExportHost from "@/components/export/GammaExportHost";
import QuotaErrorBoundary from "@/components/QuotaErrorBoundary";
import TelemetryErrorBoundary from "@/components/TelemetryErrorBoundary";
import UpdateAvailableButton from "@/components/layout/UpdateAvailableButton";
import RouteRobotsController from "@/components/seo/RouteRobotsController";
import HtmlLangController from "@/components/i18n/HtmlLangController";
import { track, setTelemetryAccount } from "@/lib/telemetry";
import { useLocation } from "@/lib/router-compat";
import { useIdlePrefetch } from "@/hooks/useIdlePrefetch";
import { dashboardImport, chatImport, calendarImport } from "@/lib/lazyPages";
import { reportLovableError } from "@/lib/lovable-error-reporting";

declare const __BUILD_ID__: string;

const BUILD_ID = typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev";

const DESCRIPTION =
  "Thiia prospecte, qualifie et relance vos contacts à votre place : CRM, emails, recherche et agenda réunis dans un agent IA souverain, hébergé en Europe.";
const TITLE = "Thiia — Agent IA de prospection pour indépendants";

const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Thiia",
  legalName: "KREAPP SAS",
  url: "https://thiia.app/",
  logo: "https://thiia.app/favicon.png",
  description:
    "Agent IA souverain de prospection pour indépendants : CRM, emails, recherche et calendrier dans un seul outil.",
  sameAs: ["https://www.thiia.fr", "https://www.thiia.app"],
};

const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Thiia",
  url: "https://thiia.app/",
  inLanguage: "fr-FR",
};

// Google Analytics (GA4) — loaded only after consent (thiia_cookie_consent.analytics).
const GA_SNIPPET = `(function () {
  var GA_ID = "G-368976CT0J";
  var CONSENT_KEY = "thiia_cookie_consent";
  window.dataLayer = window.dataLayer || [];
  function gtag(){ window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  var loaded = false;
  function loadGA() {
    if (loaded) return;
    loaded = true;
    gtag("consent", "update", { analytics_storage: "granted" });
    var s = document.createElement("script");
    s.async = true;
    s.src = "https://www.googletagmanager.com/gtag/js?id=" + GA_ID;
    document.head.appendChild(s);
    gtag("js", new Date());
    gtag("config", GA_ID, { anonymize_ip: true });
  }
  function hasAnalyticsConsent() {
    try {
      var raw = localStorage.getItem(CONSENT_KEY);
      if (!raw) return false;
      var p = JSON.parse(raw);
      return p && p.analytics === true;
    } catch (e) { return false; }
  }
  if (hasAnalyticsConsent()) loadGA();
  window.addEventListener("thiia:cookie-consent", function (e) {
    if (e && e.detail && e.detail.analytics === true) loadGA();
  });
  window.addEventListener("storage", function (e) {
    if (e.key === CONSENT_KEY && hasAnalyticsConsent()) loadGA();
  });
})();`;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1.0, viewport-fit=cover",
      },
      { name: "build-id", content: BUILD_ID },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "prospection IA, CRM indépendants, agent IA, Thiia, prospection automatisée, IA souveraine, France",
      },
      { name: "theme-color", content: "#7B2D8E" },
      { name: "author", content: "KREAPP" },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Thiia" },
      { property: "og:locale", content: "fr_FR" },
      { property: "og:url", content: "https://thiia.app/" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:image", content: "https://thiia.app/og-image.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: TITLE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: "https://thiia.app/og-image.jpg" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@300..800&family=Lora:wght@400..700&display=swap",
      },
      { rel: "icon", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/icon-180.png" },
      { rel: "mask-icon", href: "/mask-icon.svg", color: "#7B2D8E" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "canonical", href: "https://thiia.app/" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(ORGANIZATION_JSONLD) },
      { type: "application/ld+json", children: JSON.stringify(WEBSITE_JSONLD) },
      { children: GA_SNIPPET },
    ],
  }),
  errorComponent: RootErrorComponent,
  shellComponent: RootDocument,
  component: RootComponent,
});

function RootErrorComponent({ error }: ErrorComponentProps) {
  useEffect(() => {
    reportLovableError(error);
  }, [error]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 text-center">
      <div className="max-w-md space-y-3">
        <h1 className="text-lg font-semibold text-foreground">Une erreur est survenue</h1>
        <p className="text-sm text-muted-foreground">
          Nous n'avons pas pu afficher cette page. Réessayez dans un instant.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
        >
          Recharger
        </button>
      </div>
    </div>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="fr">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/** Ported from the pre-migration src/main.tsx side effects. Browser only. */
function useAppBootstrap() {
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [
        { runLocalStorageMigrations },
        { installSonnerErrorFilter },
        { installErrorReporter },
        { initTelemetryCapture },
        { installQuotaInterceptor },
        { startVersionChecker },
      ] = await Promise.all([
        import("@/lib/localStorageMigrations"),
        import("@/lib/errorToast"),
        import("@/lib/errorReporter"),
        import("@/lib/telemetryAutoInit"),
        import("@/lib/quotaInterceptor"),
        import("@/lib/versionChecker"),
      ]);
      if (cancelled) return;
      runLocalStorageMigrations();
      installSonnerErrorFilter();
      installErrorReporter();
      initTelemetryCapture();
      installQuotaInterceptor();
      startVersionChecker();
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}

const IDLE_PREFETCH_PAGES = [dashboardImport, chatImport, calendarImport];
const NO_PREFETCH: typeof IDLE_PREFETCH_PAGES = [];

/** Préchargement des pages de l'app réservé aux utilisateurs connectés :
 *  les visiteurs du site public ne téléchargent pas le code de l'application. */
function IdlePrefetcher() {
  const { user } = useAuth();
  useIdlePrefetch(user ? IDLE_PREFETCH_PAGES : NO_PREFETCH);
  return null;
}

function RouteChangeTimer() {
  const location = useLocation();
  useEffect(() => {
    const start = performance.now();
    const id = requestAnimationFrame(() => {
      const value = Math.round(performance.now() - start);
      // Filter sub-frame microswitches
      if (value > 16) {
        track("perf", "route-change", { value, route: location.pathname });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [location.pathname]);
  return null;
}

function AccountTelemetryBinder() {
  const { user } = useAuth();
  useEffect(() => {
    setTelemetryAccount(user?.id ?? null);
  }, [user?.id]);
  return null;
}

/**
 * Remonte une seule fois l'arbre de page après l'hydratation.
 *
 * Les animations de montage (framer-motion `initial`/`animate`) ne se jouent
 * pas sur un arbre hydraté : le HTML serveur contient déjà les styles initiaux
 * (`opacity: 0`) et l'élément reste invisible. Un remount unique côté client
 * rejoue ces animations sans toucher aux composants concernés.
 */
// Routes marketing concernées par les animations de montage. Ailleurs, le
// remount ferait rejouer tous les effets de page (double chargement, actions
// à usage unique déclenchées deux fois) : on s'en abstient.
const ANIMATED_PUBLIC_PATHS = ["/landing", "/use-cases", "/comparaisons", "/docs", "/contact"];

function HydrationRemount({ children }: { children: React.ReactNode }) {
  const needsRemount =
    typeof window !== "undefined" &&
    ANIMATED_PUBLIC_PATHS.some(
      (p) => window.location.pathname === p || window.location.pathname.startsWith(`${p}/`),
    );
  const [pass, setPass] = React.useState(0);
  React.useEffect(() => {
    if (!needsRemount) return;
    // setTimeout : on attend que l'hydratation (y compris les frontières
    // Suspense/lazy) soit terminée, sinon React peut fusionner ce rendu avec
    // l'hydratation et le remount n'a pas lieu.
    const id = setTimeout(() => setPass(1), 0);
    return () => clearTimeout(id);
  }, [needsRemount]);
  return <React.Fragment key={pass}>{children}</React.Fragment>;
}


function RootComponent() {
  useAppBootstrap();

  // Remonte une éventuelle session interrompue (onglet tué par le navigateur).
  useEffect(() => {
    void import("@/lib/crashBreadcrumb").then((m) => m.reportInterruptedSession());
  }, []);


  // The QueryClient lives in the router context (see src/router.tsx). Every
  // provider below (AccountProvider, BrandingProvider, ...) calls useQueryClient,
  // so the provider has to sit above them on the server as well as the client.
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
    <TooltipProvider>

      <Toaster />
      <Sonner />
      <BulkConfirmHost />
      <GdprConsentHost />
      <ConfirmDialogHost />
      <PromptDialogHost />
      <SenderPickHost />
      <TemplatePickerHost />
      <CloudTemplateExportHost />
      <ConfirmRawExportHost />
      <ExportDestinationHost />
      <GammaExportHost />
      <OfflineBanner />
      <UpdateAvailableButton />
      <QuotaErrorBoundary>
        <RouteRobotsController />
        <HtmlLangController />
        <ScrollToHash />
        <CookieConsentBanner />
        <RouteChangeTimer />
        <AuthProvider>
          <IdlePrefetcher />
          <AccountTelemetryBinder />
          <CoverFieldsHost />
          <AccountProvider>
            <ThemeProvider>
              <MobileMenuProvider>
                <BrandingProvider>
                  <ArtifactsProvider>
                    <RightPanelProvider>
                      <ImpersonationBanner />
                      <PendingDeepLinkConsumer />
                      <ArtifactsHost />
                      <TelemetryErrorBoundary>
                        <HydrationRemount>
                          <Outlet />
                        </HydrationRemount>
                      </TelemetryErrorBoundary>

                    </RightPanelProvider>
                  </ArtifactsProvider>
                </BrandingProvider>
              </MobileMenuProvider>
            </ThemeProvider>
          </AccountProvider>
        </AuthProvider>
      </QuotaErrorBoundary>
    </TooltipProvider>
    </QueryClientProvider>
  );
}
